import { useContext, useEffect, useState } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  RecurringInvestmentTransaction,
  RecurringInvestmentTransactionCollection,
} from "wealthmode-frontend/backend"
import { Text } from "wealthmode-frontend/gluestack/ui"
import { formatRecurringAmount, formatRecurringDay } from "wealthmode-frontend/util"

import { DetailField, DetailLink, ResourceDetail } from "@/components"
import { BackendContext } from "@/context"

export default function RecurringInvestmentTransactionDetail() {
  const { client_id, account_id, recurring_investment_transaction_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
    recurring_investment_transaction_id: string
  }>()

  const backend = useContext(BackendContext)

  const [transaction, setTransaction] = useState<RecurringInvestmentTransaction>()

  useEffect(() => {
    ;(async () => {
      const clients = backend.collection("clients") as ClientCollection
      const client = clients.model(client_id)
      await client.fetch({ reload: true })

      const accounts = client.link("accounts")?.resolve() as AccountCollection
      const account = accounts.model(account_id)
      await account.fetch({ reload: true })

      const data = account.embed("data") as AccountData
      const transactions = data.link("recurring_investment_transactions")?.resolve() as
        RecurringInvestmentTransactionCollection | undefined
      if (!transactions) {
        return
      }

      const model = transactions.model(recurring_investment_transaction_id)
      await model.fetch({ reload: true })

      setTransaction(model)
    })().catch((err) => {
      console.error("Error: " + err)
      throw err
    })
  }, [backend, client_id, account_id, recurring_investment_transaction_id])

  const base = `/clients/${client_id}/accounts/${account_id}/recurring_investment_transactions/${recurring_investment_transaction_id}`

  const fields: DetailField[] = []
  const links: DetailLink[] = [{ href: `${base}/occurrences`, label: "Occurrences" }]

  if (transaction) {
    const instrument = transaction.instrument()

    fields.push(
      { label: "Frequency", value: transaction.attributes.frequency },
      { label: "Interval", value: transaction.attributes.interval },
      { label: "Day", value: formatRecurringDay(transaction.attributes) },
      { label: "End", value: transaction.attributes.end_date ?? "" },
      { label: "Category", value: transaction.attributes.transaction_category },
      { label: "Kind", value: transaction.attributes.transaction_kind },
      {
        label: "Instrument",
        value:
          instrument == null ? (
            ""
          ) : (
            <Link href={`/instruments/${instrument.attributes.id}`}>
              <Text className="text-markdown-link hover:opacity-80 underline">
                {instrument.attributes.symbol ??
                  (instrument.attributes.type == "cash" ? "Cash" : instrument.attributes.type)}
              </Text>
            </Link>
          ),
      },
      { label: "Amount Type", value: transaction.attributes.amount_type },
      { label: "Amount", value: formatRecurringAmount(transaction.attributes) },
    )
  }

  return (
    <ResourceDetail
      title={transaction?.attributes.description || "Recurring Investment Transaction"}
      subtitle={recurring_investment_transaction_id}
      loading={!transaction}
      fields={fields}
      links={links}
    />
  )
}
